import * as React from 'react';
import ChevronRightIcon from '@mui/icons-material/ChevronRightRounded';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import clsx from 'clsx';
import { useState, type MouseEvent } from 'react';
import { PanelLeft, SquareCheck } from 'lucide-react';
import { useFlags } from '@flagsmith/flagsmith/react';

import { Flex, FlexColumn } from 'src/components/flex';
import { DesktopNavigationInboxesLink } from 'src/components/layout/landlord/DesktopNavigationInboxesLink';
import { DesktopNavigationPropertiesLink } from 'src/components/layout/landlord/DesktopNavigationPropertiesLink';
import { useLandlordLayoutContext } from 'src/components/layout/landlord/LandlordLayoutProvider';
import { links } from 'src/components/layout/landlord/links';
import NavigationLink, { FeatureFlagNavLink } from 'src/components/layout/landlord/NavigationLink';
import Link from 'src/components/link/Link';
import { ProfileImageRounded } from 'src/components/ProfileImageRounded';
import Responsive from 'src/components/responsive/Responsive';
import { LANDLORD_HOME_PATH } from 'src/routes';
import { useLoggedInUser } from 'src/services/api';
import { ThemeProvider } from 'src/theme';
import { LandlordTeamSelectDialog } from 'src/components/layout/landlord/LandlordTeamSelect';
import NavigationSidebarMenu from 'src/components/layout/landlord//NavigationSidebarMenu';
import ActivityCalendar from 'src/components/layout/landlord/ActivityCalendar';
import { EFeatureFlag } from 'src/utils/feature-flags';

import navbarStyles from 'src/components/layout/landlord/DesktopNavigationSidebar.module.css';
import layoutStyles from 'src/components/layout/landlord/LandlordLayout.module.css';


type Props = {
    onNavigationChange: () => void;
}

export default function DesktopNavigationSidebar({
    onNavigationChange
}: Props) {
    const { isSidebarCollapsed, setSidebarCollapsed, sidebarWidth } = useLandlordLayoutContext();
    const { data: loggedInUser } = useLoggedInUser();
    const [isProfileMenuOpened, setIsProfileMenuOpened] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isTeamSwitcherOpen, setIsTeamSwitcherOpen] = useState(false);
    const flags = useFlags([EFeatureFlag.FEATURE_FLAG_ROUTE, EFeatureFlag.TRIAGE_INBOX_PAGE]);

    const handleClickSidebar = () => {
        setSidebarCollapsed(!isSidebarCollapsed);
    };


    const handleOpenProfileMenu = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setIsProfileMenuOpened(true);
    };

    const handleOpenTeamSwitcher = () => {
        setIsTeamSwitcherOpen(true);
        setIsProfileMenuOpened(false);
    };

    return (
        <>
            <Responsive desktop>
                <Box
                    className={clsx(
                        layoutStyles.sidebar,
                        layoutStyles.sidebarPadding,
                    )}
                    sx={{
                        width: sidebarWidth,
                        minWidth: sidebarWidth,
                    }}
                />
            </Responsive>

            <ThemeProvider>
                <Responsive desktop>
                    <Paper
                        square
                        className={layoutStyles.sidebar}
                        sx={{ width: sidebarWidth, minWidth: sidebarWidth }}
                    >
                        <FlexColumn height="100vh" justifyContent="space-between" className={navbarStyles.navbarFlexContainer}>
                            {/* this is the navigation bar header */}
                            <Flex flexDirection={isSidebarCollapsed ? 'column' : 'row'} justifyContent="space-between" pr={0.5}>
                                <Link href={LANDLORD_HOME_PATH} data-testid="home-link">
                                    <Flex height={60} p={1} alignItems="center">
                                        <img
                                            src="/img/reffie-logos/brain-transparent-256.png"
                                            alt="reffie-logo"
                                            height="40px"
                                        />
                                        {!isSidebarCollapsed && (
                                            <Typography color="primary" fontWeight="bold">
                                                Reffie
                                            </Typography>
                                        )}
                                    </Flex>
                                </Link>

                                <IconButton
                                    onClick={handleClickSidebar}
                                    data-testid="collapse-toggle"
                                    sx={{
                                        width: isSidebarCollapsed ? 60 : 'auto',
                                    }}
                                >
                                    <PanelLeft size={22} />
                                </IconButton>
                            </Flex>

                            {/* these are the main nav links */}
                            <FlexColumn className="prettyScrollbar" flex={1} sx={{ overflowY: 'auto' }}>
                                <List>
                                    {flags.feature_flag_route?.enabled && <FeatureFlagNavLink onNavigationChange={onNavigationChange} />}
                                    {flags.triage_inbox_page?.enabled && (
                                        <NavigationLink
                                            text={isSidebarCollapsed ? '' : 'Bulk Triage'}
                                            icon={<SquareCheck />}
                                            link="/landlord/inner/bulk-triage"
                                            onClick={onNavigationChange}
                                        />
                                    )}
                                    <DesktopNavigationInboxesLink onNavigationChange={onNavigationChange} isSidebarCollapsed={isSidebarCollapsed} />
                                    {links.map((link) => (
                                        link.text === 'Properties' ? (
                                            <DesktopNavigationPropertiesLink
                                                key="properties"
                                                isSidebarCollapsed={isSidebarCollapsed}
                                                onNavigationChange={onNavigationChange}
                                            />
                                        ) : (
                                            <NavigationLink
                                                key={link.text}
                                                text={isSidebarCollapsed ? '' : link.text}
                                                icon={link.icon}
                                                link={link.link}
                                                onClick={onNavigationChange}
                                            />
                                        )
                                    ))}
                                </List>
                            </FlexColumn>

                            <FlexColumn>
                                {!isSidebarCollapsed &&
                                    <Box sx={{ px: 1.5 }}>
                                        <ActivityCalendar />
                                    </Box>
                                }
                                {loggedInUser && (
                                    <Flex px={1} py={0} alignItems="center" gap={1} flexDirection={isSidebarCollapsed ? 'column' : 'row'} sx={{
                                        cursor: 'pointer',
                                        userSelect: 'none'
                                    }} onClick={handleOpenProfileMenu} position="relative" data-testid="user-account-menu-toggle"
                                    >
                                        <ProfileImageRounded url={loggedInUser.profile_photo_url} userName={loggedInUser.name} width={32} height={32} />
                                        {!isSidebarCollapsed && (
                                            <>
                                                <FlexColumn alignItems="start" px={1} py={0.5} gap={0.38} textAlign="start"
                                                    justifyContent="start"
                                                    overflow="hidden"
                                                >
                                                    {/* NOTE: using tooltips in case of long names */}
                                                    <Tooltip title={loggedInUser.name}>
                                                        <Typography variant="body2" fontWeight={600} fontSize={14} width="100%"
                                                            color="rgba(66, 64, 58, 1)}" className="ellipsisText"
                                                        >{loggedInUser.name}</Typography>
                                                    </Tooltip>
                                                    {/* NOTE: all landlord users that have completed onboarding have a team */}
                                                    <Tooltip title={loggedInUser?.team?.name || ''}>
                                                        <Typography variant="body2" fontWeight={400} fontSize={14} width="100%"
                                                            color="rgba(158, 157, 152, 1)" className="ellipsisText"
                                                        >{loggedInUser?.team?.name || ''}</Typography>
                                                    </Tooltip>
                                                </FlexColumn>
                                                <ChevronRightIcon color="secondary" sx={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: 0 }} />
                                            </>
                                        )}
                                    </Flex>)}
                                {isTeamSwitcherOpen && <LandlordTeamSelectDialog open={isTeamSwitcherOpen} onClose={() => setIsTeamSwitcherOpen(false)} />}
                                {isProfileMenuOpened && (
                                    <NavigationSidebarMenu
                                        anchorEl={anchorEl}
                                        setIsProfileMenuOpened={setIsProfileMenuOpened}
                                        isSidebarCollapsed={isSidebarCollapsed}
                                        loggedInUser={loggedInUser!}
                                        openTeamSwitcher={handleOpenTeamSwitcher}
                                    />
                                )}
                            </FlexColumn>
                        </FlexColumn>
                    </Paper>
                </Responsive>
            </ThemeProvider>
        </>
    );
}
